import { useQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { getQueryParams } from 'utils/getQueryParams'
import dayjs from 'dayjs'
import { DiscrepancyRecord } from '../types'
import {
  STOCK_DISCREPANCIES_QUERY_KEY,
  STOCK_DISCREPANCIES_URL
} from './queryConstants'

type StockDiscrepanciesResponse = {
  records: DiscrepancyRecord[]
  total: number
  skip: number
  limit: number
  hasMore: boolean
}

type UseStockDiscrepanciesProps = {
  startDate?: dayjs.Dayjs
  endDate?: dayjs.Dayjs
  status?: 'PENDING' | 'RECONCILED' | 'INVESTIGATING'
  limit?: number
  residentId?: string[]
  enabled?: boolean
}

type UseStockDiscrepanciesReturn = {
  data: StockDiscrepanciesResponse | null
  isLoading: boolean
  refetch: () => void
}

export const useStockDiscrepancies = ({
  startDate,
  endDate,
  status,
  limit,
  residentId,
  enabled = true
}: UseStockDiscrepanciesProps): UseStockDiscrepanciesReturn => {
  const formattedStartDate = startDate
    ? dayjs(startDate).startOf('day').format('YYYY-MM-DD')
    : dayjs().subtract(30, 'days').startOf('day').format('YYYY-MM-DD')

  const formattedEndDate = endDate
    ? dayjs(endDate).endOf('day').format('YYYY-MM-DD')
    : dayjs().endOf('day').format('YYYY-MM-DD')

  const params = getQueryParams({
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    ...(status && { status }),
    ...(limit !== undefined && { limit: limit.toString() }),
    ...(residentId && residentId.length > 0 && { residentId })
  })

  const { data, isLoading, refetch } = useQuery(
    [
      STOCK_DISCREPANCIES_QUERY_KEY,
      formattedStartDate,
      formattedEndDate,
      status,
      limit,
      residentId
    ],
    () =>
      getJSON({
        url: `${STOCK_DISCREPANCIES_URL}?${params}`
      }),
    {
      enabled,
      retry: 1
    }
  )

  return {
    data: (data as StockDiscrepanciesResponse) || null,
    isLoading,
    refetch
  }
}

export default useStockDiscrepancies
